/*
=============================================================================
FILE: sporteyes-custom-complete.js
MERGED FROM:
  - sporteyes-custom.js (1,206 lines)
  - variant-gallery.js (644 lines)

PURPOSE:
  Complete JavaScript enhancement system for Shopify APO form initialization,
  interaction, and variant gallery slider with PD measurement guide modal.
  
  Single file combining all JavaScript enhancements for optimal performance:
  - Reduces HTTP requests (1 request instead of 2)
  - Easier maintenance (single file to manage)
  - Preserves all original functionality

LOAD ORDER (6 Systems + Components):
  0. SporteyesLogger (foundation - all systems use this)
  1. Text Limiter (15-word description truncation)
  2. Prescription Form Detection & Enhancement (Systems 2-5 combined)
  3. VariantGallery Web Component
  4. PD Guide Link Injector
  5. PD Guide Modal System

SYSTEMS OVERVIEW:
  0. SPORTEYES LOGGER
     - Dev/prod-aware logging (window.SporteyesDebug = true)
     - Optional Make.com webhook for critical errors
     - Rate-limited error reporting

  1. TEXT LIMITER
     - Truncates descriptions to 15 words
     - Creates "Read More/Less" toggle button

  2. PRESCRIPTION FORM DETECTION
     - Pattern-based field detection (OD, OS, Cylinder, Axis, PD, ADD)
     - Adds layout classes (.prescription-3col, .prescription-full-width)

  3. FORM STEP DETECTION
     - Detects form layout style (card vs. form input)
     - Auto-height adjustment for text input steps

  4. RADIO/CHECKBOX CARD CLICKABILITY
     - Makes full card clickable (non-intrusive)
     - Preserves native input behavior

  5. IMAGE LAYOUT
     - DOM restructuring: moves image before content stack
     - Merged radio/checkbox handler

COMPONENTS:
  - VariantGallery: Web Component (`<variant-gallery>`) for product image slider
  - PD Link Injector: Pattern-based detection of PD fields with link injection
  - PD Modal: Global modal system for displaying prescription measurement guides

CSS ARCHITECTURE:
  All CSS is defined in sporteyes-custom.css.
  This file does NOT inject any <style> tags. Zero runtime CSS injection.

DEPLOYMENT:
  - Update Shopify asset references to use sporteyes-custom-complete.js
  - Remove original variant-gallery.js from Shopify assets
  - Keep sporteyes-custom.css (unchanged)
  - Optional: Keep original files as backup/reference

=============================================================================
*/


/* =========================================================================
   SYSTEM 0: SPORTEYES LOGGER
   FOUNDATION - All systems depend on this
   - All console.log calls go through here.
   - window.SporteyesDebug = true  → enables dev logging in browser console
   - Critical errors auto-report via Make.com webhook (sendBeacon, non-blocking)
   - Rate-limited: one webhook per unique message per page-view
   - Exposed as window.SporteyesLogger for use by all systems
   ========================================================================= */
const SporteyesLogger = (function () {
  'use strict';

  // Paste your Make.com webhook URL here after creating the scenario.
  // Leave as empty string to disable webhook (logger still works for dev mode).
  const WEBHOOK_URL = 'https://hook.eu1.make.com/7h9mw18mebdel52bv1smgogludqki78a';

  const PAGE_URL    = window.location.href;
  const VARIANT_ID  = new URLSearchParams(window.location.search).get('variant') || '';
  const _sent       = new Set(); // rate-limit: one report per message per page-view

  function sendToMake(message, error, context) {
    if (!WEBHOOK_URL) return;
    const key = message.substring(0, 50);
    if (_sent.has(key)) return;
    _sent.add(key);

    try {
      // Build payload with all available error details
      const payload = {
        severity:     'critical',
        message:      message || 'Unknown error',
        errorMessage: error ? (error.message || String(error)) : 'No error details',
        errorStack:   error ? (error.stack || 'No stack trace') : '',
        errorName:    error ? (error.name || 'Unknown error type') : '',
        page:         PAGE_URL || window.location.href || 'Unknown page',
        variant:      VARIANT_ID || 'N/A',
        userAgent:    navigator.userAgent || 'Unknown browser',
        timestamp:    new Date().toISOString(),
        context:      context ? JSON.stringify(context) : 'No context'
      };

      // Send as form-encoded data (Make.com compatible format)
      // Option 1: Use FormData (best for Make.com)
      if (typeof FormData !== 'undefined') {
        const formData = new FormData();
        for (const [key, value] of Object.entries(payload)) {
          formData.append(key, value);
        }
        navigator.sendBeacon(WEBHOOK_URL, formData);
      } else {
        // Option 2: Fallback to URL-encoded string
        const encoded = Object.entries(payload)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');
        navigator.sendBeacon(WEBHOOK_URL, encoded);
      }
    } catch (e) {
      // Never throw from the logger
      // Attempt simple text fallback
      try {
        navigator.sendBeacon(WEBHOOK_URL, `error=${encodeURIComponent(message)}&status=failed`);
      } catch (e2) {
        // Silent fail
      }
    }
  }

  return {
    /** Dev-only log (requires window.SporteyesDebug = true) */
    log: function (msg, ...args) {
      if (window.SporteyesDebug) console.log('[Sporteyes]', msg, ...args);
    },
    /** Dev-only warn (requires window.SporteyesDebug = true) */
    warn: function (msg, ...args) {
      if (window.SporteyesDebug) console.warn('[Sporteyes WARN]', msg, ...args);
    },
    /** Dev-only error + Make.com webhook */
    error: function (msg, err, context) {
      if (window.SporteyesDebug) console.error('[Sporteyes ERROR]', msg, err);
      sendToMake(msg, err, context);
    },
    /** Always-visible critical error + Make.com webhook */
    critical: function (msg, err, context) {
      console.error('[Sporteyes CRITICAL]', msg);
      sendToMake(msg, err, context);
    }
  };
})();

// Expose for use by all systems (variants, modals, etc.)
window.SporteyesLogger = SporteyesLogger;

// Add public test method for debugging
window.testSporteyesLogger = function(type = 'error') {
  if (type === 'error') {
    const testError = new Error('Test error from Sporteyes');
    SporteyesLogger.error('Test error message - check your email for this!', testError, {
      testData: 'This is test context data',
      pagePath: window.location.pathname
    });
    console.log('[Sporteyes Test] Error log sent to Make.com webhook');
  } else if (type === 'critical') {
    SporteyesLogger.critical('Test critical error from Sporteyes', new Error('Critical test'), {
      testType: 'critical'
    });
    console.log('[Sporteyes Test] Critical error sent to Make.com webhook');
  }
};

// Catch uncaught errors originating from our scripts
// DISABLED: Removed global error handler as it was reporting empty/meaningless errors
// Real issues are caught by try/catch blocks in each system (runCombinedSystem, etc.)
// This handler was creating noise without providing useful debugging info


/* =========================================================================
   GLOBAL UTILITIES
   Shared helpers used by multiple systems (IIFEs)
   ========================================================================= */

/**
 * Debounce utility — delay function execution until after wait ms of inactivity
 * Used by: Text Limiter, Combined Systems, PD Injector, VariantGallery
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Async settlement helper for clean async/await chains
 * Replaces manual setTimeout(..., ms) with await settle(ms)
 */
const settle = (ms) => new Promise((resolve) => setTimeout(resolve, ms));


/* =========================================================================
   SYSTEM 1: TEXT LIMITER
   Truncates long option descriptions to 15 words with a "Read More" toggle.
   Targets: .mw-option__value .mw-stack__item inside radio/checkbox cards.

   CSS is in sporteyes-custom.css (Section 4 — Text Limiter).
   This IIFE does NOT inject any <style> tags.
   ========================================================================= */
(function () {
  'use strict';

  // Uses global debounce() defined at module level

  // ===== CONFIGURATION =====
  const CONFIG = {
    wordLimit:        15,
    readMoreText:    'Read More',
    readLessText:    'Read Less',
    animationDuration: 300,
    targetSelector:  '.mw-option__value .mw-stack__item',
    excludeSelectors: ['.mw-option__value-title', '.mw-option__media', 'label', 'input']
  };

  // ===== TEXT UTILITIES =====

  function countWords(text) {
    const trimmed = text.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  }

  function truncateText(text, wordLimit) {
    const words = text.trim().split(/\s+/);
    return words.length <= wordLimit
      ? { truncated: text, hasMore: false }
      : { truncated: words.slice(0, wordLimit).join(' '), hasMore: true };
  }

  // ===== TOGGLE BUTTON =====
  // Styles are defined in CSS (Section 4). Inline styles are only used for
  // overriding APO's pointer-events restrictions — CSS cannot override inline styles.
  function createToggleButton(text, onClick) {
    const button = document.createElement('button');
    button.className = 'text-limiter-toggle';
    button.textContent = text;
    button.type = 'button';
    button.setAttribute('data-clickable', 'true');

    // These inline styles override APO's `pointer-events: none !important` on card children.
    // The CSS class handles all visual styling.
    button.style.setProperty('pointer-events', 'auto', 'important');
    button.style.setProperty('z-index', '999', 'important');
    button.style.setProperty('position', 'relative', 'important');

    // OPTIMIZATION: CSS :hover handles color transitions (eliminates reflow from inline style writes)
    // Native browser hover is faster than JavaScript style manipulation

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onClick();
    };

    button.addEventListener('click',      handleClick, true);
    button.addEventListener('mousedown',  handleClick, true);
    button.addEventListener('touchstart', handleClick, { capture: true, passive: false });

    return button;
  }

  // ===== PROCESS INDIVIDUAL ELEMENT =====
  function processTextElement(element) {
    if (element.dataset.textLimiterProcessed === 'true') return;

    const originalText = element.textContent.trim();
    if (countWords(originalText) <= CONFIG.wordLimit) return;

    element.dataset.textLimiterProcessed = 'true';
    element.dataset.originalText = originalText;

    const { truncated } = truncateText(originalText, CONFIG.wordLimit);

    const container = document.createElement('div');
    container.className = 'text-limiter-container';
    // Minimal inline style to override APO pointer-events; visual handled by CSS class
    container.style.setProperty('pointer-events', 'auto', 'important');

    const textSpan = document.createElement('span');
    textSpan.className = 'text-limiter-text';
    textSpan.textContent = truncated + '...';

    let isExpanded = false;

    function toggleText() {
      isExpanded = !isExpanded;
      if (isExpanded) {
        textSpan.textContent = originalText + ' ';
        button.textContent = CONFIG.readLessText;
      } else {
        textSpan.textContent = truncated + '...';
        button.textContent = CONFIG.readMoreText;
      }
    }

    const button = createToggleButton(CONFIG.readMoreText, toggleText);

    element.innerHTML = '';
    container.appendChild(textSpan);
    container.appendChild(button);
    element.appendChild(container);

    element.style.setProperty('pointer-events', 'auto', 'important');
  }

  // ===== PROCESS ALL CARDS =====
  function processCardDescriptions() {
    const allCards = document.querySelectorAll(
      '.mw-groups .mw-option--type-radio .mw-option__value, ' +
      '.mw-groups .mw-option--type-checkbox .mw-option__value'
    );

    allCards.forEach(card => {
      const descriptionElements = card.querySelectorAll(CONFIG.targetSelector);

      descriptionElements.forEach(element => {
        if (CONFIG.excludeSelectors.some(sel =>
          element.matches(sel) || element.closest(sel)
        ) || element.querySelector('input, label, .mw-option__value-title')) {
          return;
        }

        const textContent = element.textContent.trim();
        if (textContent.length > 50 && countWords(textContent) > CONFIG.wordLimit) {
          processTextElement(element);
        }
      });
    });
  }

  // ===== INITIALIZATION =====
  function initializeTextLimiter() {
    try {
      // CSS is already loaded (sporteyes-custom.css) — no style injection needed
      processCardDescriptions();
    } catch (error) {
      SporteyesLogger.error('initializeTextLimiter failed', error);
    }
  }

  function reinitializeTextLimiter() {
    setTimeout(() => {
      try {
        processCardDescriptions();
      } catch (error) {
        SporteyesLogger.error('reinitializeTextLimiter failed', error);
      }
    }, 100);
  }

  // DOM-ready initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTextLimiter);
  } else {
    initializeTextLimiter();
  }

  // MutationObserver: re-run when APO content changes
  // FIXED: Narrow from document.body to .mw-apo-container or closest parent to reduce observer firing frequency
  const debouncedReinitialize = debounce(reinitializeTextLimiter, 150);

  const textLimiterObserver = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && (
            (node.matches && node.matches('.mw-groups, .mw-option--type-radio, .mw-option--type-checkbox')) ||
            (node.querySelector && node.querySelector('.mw-groups'))
          )) {
            debouncedReinitialize();
            return;
          }
        }
      }
    }
  });

  // Observe .mw-apo-container if available, otherwise fallback to document.body
  const textLimiterTarget = document.querySelector('.mw-apo-container') || document.body;
  textLimiterObserver.observe(textLimiterTarget, { childList: true, subtree: true });

  // Deferred safety pass (APO may load after initial DOM-ready)
  setTimeout(initializeTextLimiter, 1000);
  setTimeout(reinitializeTextLimiter, 3000);

  // ===== PUBLIC API =====
  window.textLimiter = {
    process:      processCardDescriptions,
    reinitialize: reinitializeTextLimiter,
    setWordLimit: (limit) => {
      CONFIG.wordLimit = limit;
      reinitializeTextLimiter();
    },
    setTexts: (readMore, readLess) => {
      CONFIG.readMoreText = readMore;
      CONFIG.readLessText = readLess;
    },
    // Return frozen copy — prevents external mutation of internal config
    getConfig: () => Object.freeze({ ...CONFIG }),
    version: '2.0.0'
  };

})();


/* =========================================================================
   SYSTEMS 2–5: PRESCRIPTION FORM, FORM STEPS, CARD CLICKABILITY, IMAGE LAYOUT
   Unified APO form enhancement system.

   CSS is in sporteyes-custom.css.
   This IIFE does NOT inject any <style> tags.
   ========================================================================= */
(function () {
  'use strict';

  // Uses global debounce() defined at module level

  // ===== CONFIGURATION =====
  const FIELD_PATTERNS = {
    threeColumn: ['od', 'os', 'right ', 'left eye', 'cylinder', 'cyclinder', 'cyl', 'axis', 'axe'], // 'cyclinder' is intentional: matches common misspelling
    fullWidth:   ['add', 'pd', 'pupil distance', 'guide', 'upload', 'file', 'distance', 'measure', 'prescription']
  };

  // DEAD CODE: LAYOUT_OPTIONS is set by setLayoutPreset() but values are never read.
  // Kept for now as part of public API, but none of the layout values are actually used.
  const LAYOUT_OPTIONS = {
    imagePosition: 'left',
    imageSize:     'medium',
    layoutStyle:   'card',
    spacing:       'normal'
  };

  // ===== STATE =====
  let currentStep             = 0; // eslint-disable-line no-unused-vars
  let prescriptionToggleState          = null;
  let changeListenerAttached  = false;
  let continuousMonitorInterval = null;

  // ===== UTILITIES =====

  /**
   * MutationObserver-based element waiter.
   * Replaces the previous 100ms polling setInterval — resolves immediately
   * when the element appears, not after an arbitrary polling delay.
   * IMPROVED: Smarter timeout handling and better observer targeting
   */
  function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);

      let rejected = false;
      const observer = new MutationObserver(() => {
        const found = document.querySelector(selector);
        if (found && !rejected) {
          observer.disconnect();
          clearTimeout(timeoutId);
          resolve(found);
        }
      });

      // Use document.body for broad coverage (more reliable than waiting for .mw-apo-container)
      observer.observe(document.body, { childList: true, subtree: true });
      
      const timeoutId = setTimeout(() => {
        rejected = true;
        observer.disconnect();
        reject(new Error(`waitForElement timeout (${timeout}ms): ${selector}`));
      }, timeout);
    });
  }

  /**
   * Wait for multiple selectors using MutationObserver-based waitForElement.
   */
  function waitForElements(selectors, timeout = 10000) {
    return Promise.all(selectors.map(sel => waitForElement(sel, timeout)));
  }

  function containsPattern(text, patterns) {
    const lowerText = text.toLowerCase();
    return patterns.some(pattern => lowerText.includes(pattern));
  }


  // ===== SYSTEM 2: PRESCRIPTION FORM DETECTION =====

  function detectPrescriptionFields() {
    try {
      const customOptions = document.querySelectorAll(
        '.mw-groups .mw-option[data-option-id]:not(.mw-option--type-radio):not(.mw-option--type-checkbox):not(.mw-option--type-button)'
      );

      customOptions.forEach(option => {
        const label = option.querySelector('.mw-label .mw-text, .mw-label');
        if (!label) return;

        const labelText  = label.textContent || label.innerText || '';
        const lowerLabel = labelText.toLowerCase();

        if (containsPattern(labelText, FIELD_PATTERNS.threeColumn)) {
          option.classList.add('prescription-3col');
          if (lowerLabel.includes('cylinder') || lowerLabel.includes('cyclinder')) {
            option.classList.add('auto-detected-cylinder-field');
          } else if (lowerLabel.includes('axis')) {
            option.classList.add('auto-detected-axis-field');
          } else {
            option.classList.add('auto-detected-eye-field');
          }
        } else if (containsPattern(labelText, FIELD_PATTERNS.fullWidth)) {
          option.classList.add('prescription-full-width', 'auto-detected-full-width-field');
        }
      });
    } catch (error) {
      SporteyesLogger.error('detectPrescriptionFields failed', error);
    }
  }

  // applyFallbackStyling removed — CSS Section 15 @supports already handles all fallback cases


  // ===== SYSTEM 3: FORM STEP DETECTION =====

  function detectAndMarkFormSteps() {
    try {
      const multistepContainer = document.querySelector('.prescription-multistep');
      if (!multistepContainer) return;

      const allSteps = document.querySelectorAll('.prescription-multistep .mw-group');

      allSteps.forEach(step => {
        const optionsContainer = step.querySelector('.mw-group__options');
        if (!optionsContainer) return;

        const hasSelectFields = optionsContainer.querySelector('.mw-option--type-select');
        const hasTextFields   = optionsContainer.querySelector('.mw-option--type-text');
        const hasFileFields   = optionsContainer.querySelector('.mw-option--type-file');

        if (hasSelectFields || hasTextFields || hasFileFields) {
          optionsContainer.classList.add('form-step-detected');

          // JS fallback for browsers without :has() — apply auto height directly
          if (optionsContainer.classList.contains('mw-group__fixed-height')) {
            optionsContainer.style.height    = 'auto';
            optionsContainer.style.minHeight = 'auto';
            optionsContainer.style.maxHeight = 'none';
            optionsContainer.style.overflow  = 'visible';
          }
        }
      });
    } catch (error) {
      SporteyesLogger.error('detectAndMarkFormSteps failed', error);
    }
  }


  // ===== PRESCRIPTION RADIO AUTO-DETECTION =====

  function detectRadioStructure() {
    const allRadios = document.querySelectorAll('.mw-groups input[type="radio"]');
    const radioGroups = {};

    allRadios.forEach(radio => {
      const groupName      = radio.name;
      const label          = radio.closest('label') || document.querySelector(`.mw-groups label[for="${radio.id}"]`);
      const valueContainer = radio.closest('.mw-option__value');

      if (!radioGroups[groupName]) radioGroups[groupName] = [];

      radioGroups[groupName].push({
        radio:     radio,
        id:        radio.id,
        value:     radio.value,
        label:     label ? (label.textContent || label.innerText || '').toLowerCase().trim() : '',
        container: valueContainer
      });
    });

    for (const [groupName, radios] of Object.entries(radioGroups)) {
      const hasNonPrescription = radios.some(r =>
        r.label.includes('non prescription') ||
        r.label.includes('non-prescription') ||
        r.label.includes('no prescription')
      );
      const hasPrescription = radios.some(r =>
        r.label.includes('prescription') && !r.label.includes('non')
      );

      if (hasNonPrescription && hasPrescription) {
        prescriptionToggleState = {
          groupName: groupName,
          nonPrescription: radios.find(r =>
            r.label.includes('non prescription') ||
            r.label.includes('non-prescription') ||
            r.label.includes('no prescription')
          ),
          prescription: radios.find(r =>
            r.label.includes('prescription') && !r.label.includes('non')
          ),
          atHome: radios.find(r =>
            r.label.includes('at home') || r.label.includes('try on')
          )
        };
        return prescriptionToggleState;
      }
    }

    return null;
  }


  // ===== MODULE-LEVEL updateUI =====
  // Queries fresh DOM refs on every call — never uses closures that could become stale
  // after APO re-renders (e.g. color swatch switch replaces DOM nodes).
  function updateUI() {
    try {
      if (!prescriptionToggleState) {
        return;
      }

      const progressBar   = document.querySelector('.mw-group__progress');
      const nextButton    = document.querySelector('.mw-group__button-next');
      const mainContainer = document.querySelector('.mw-group__container');

      // Find checked radio by group name
      const checkedRadio = document.querySelector(
        `.mw-groups input[name="${prescriptionToggleState.groupName}"]:checked`
      );

      if (!checkedRadio) {
        if (nextButton) { nextButton.textContent = 'Add to Cart'; nextButton.classList.add('disabled'); }
        if (progressBar) progressBar.classList.remove('show-progress');
        if (mainContainer) mainContainer.classList.remove('prescription-multistep');
        return;
      }

      // Detect by LABEL TEXT — stable across APO re-renders (IDs change, text doesn't)
      const card          = checkedRadio.closest('.mw-option__value');
      const cardLabelEl   = card ? (card.querySelector('.mw-text, label, .mw-label') || card) : null;
      const cardLabelText = cardLabelEl ? cardLabelEl.textContent.toLowerCase().trim() : '';
      const isPrescription = cardLabelText.includes('prescription') &&
        !cardLabelText.includes('non') &&
        !cardLabelText.includes('at home');

      if (isPrescription) {
        if (progressBar)   progressBar.classList.add('show-progress');
        if (nextButton)    { nextButton.textContent = 'Next'; nextButton.classList.remove('disabled'); }
        if (mainContainer) mainContainer.classList.add('prescription-multistep');
      } else {
        if (progressBar)   progressBar.classList.remove('show-progress');
        if (nextButton)    { nextButton.textContent = 'Add to Cart'; nextButton.classList.remove('disabled'); }
        if (mainContainer) mainContainer.classList.remove('prescription-multistep');
      }
    } catch (e) {
    }
  }

  function initPrescriptionToggle() {
    try {
      if (!prescriptionToggleState) {
        prescriptionToggleState = detectRadioStructure();
        if (!prescriptionToggleState) {
          return;
        }
      }

      const progressBar = document.querySelector('.mw-group__progress');
      const nextButton  = document.querySelector('.mw-group__button-next');

      if (!progressBar || !nextButton) {
        return;
      }

      // Attach change listener exactly ONCE — prevents duplicate listeners stacking
      // up on every APO re-render (swatch switch)
      if (!changeListenerAttached) {
        changeListenerAttached = true;

        document.addEventListener('change', function (e) {
          if (!prescriptionToggleState) return; // APO is re-rendering — skip safely
          if (e.target && e.target.name === prescriptionToggleState.groupName) {
            setTimeout(updateUI, 100);
            setTimeout(() => updateRadioSelectionStates(e.target), 200);
          }
        }, { passive: true });
      }

      setTimeout(updateUI, 200);

    } catch (error) {
      SporteyesLogger.critical('universalToggle failed', error, {
        groupName: prescriptionToggleState ? prescriptionToggleState.groupName : 'N/A'
      });
    }
  }


  // ===== SELECTION VISUAL STATES =====
  function updateRadioSelectionStates(selectedRadio) {
    try {
      const groupName        = selectedRadio.name;
      const allRadiosInGroup = document.querySelectorAll(`.mw-groups input[name="${groupName}"]`);

      allRadiosInGroup.forEach(radio => {
        const optionValue = radio.closest('.mw-option__value');
        if (optionValue) {
          if (radio.checked) {
            optionValue.classList.add('mw-option__value--selected');
          } else {
            optionValue.classList.remove('mw-option__value--selected');
          }
        }
      });
    } catch (error) {
      SporteyesLogger.error('updateRadioSelectionStates failed', error);
    }
  }


  // ===== SYSTEM 4: CARD CLICKABILITY =====

  /**
   * Make cards clickable (both radio and checkbox)
   * @param {string} inputType - 'radio' or 'checkbox'
   */
  function makeCardsClickable(inputType) {
    try {
      const selector = `.mw-groups .mw-option--type-${inputType} .mw-option__value`;
      const inputSelector = `input[type="${inputType}"]`;
      const cards = document.querySelectorAll(selector);

      cards.forEach(card => {
        if (card.hasAttribute('data-clickable-enhanced')) return;
        card.setAttribute('data-clickable-enhanced', 'true');

        card.addEventListener('click', function (e) {
          const target = e.target;
          const inputButton = card.querySelector(inputSelector);
          const label = card.querySelector('label');

          if (target === inputButton || target === label ||
              target.closest('label') || target.closest(inputSelector)) {
            return; // Let APO handle natively
          }

          if (inputButton && (inputType === 'checkbox' || !inputButton.checked)) {
            inputButton.click();
          }
        }, { passive: true });
      });
    } catch (error) {
    }
  }

  function makeRadioCardsClickable() {
    makeCardsClickable('radio');
  }

  function makeCheckboxCardsClickable() {
    makeCardsClickable('checkbox');
  }


  // ===== SYSTEM 5: IMAGE LAYOUT =====
  /*
   * applyImageLayout: Unified function for both radio AND checkbox cards.
   * Previously there were two nearly-identical functions (applyLayoutToOption
   * and applyLayoutToCheckboxOption) — merged into one parameterized function.
   *
   * This function ONLY restructures the DOM (moves media element before content).
   * All visual styling (flex, border, padding, image size) is in sporteyes-custom.css.
   * No inline styles are applied here except the data-attribute marker.
   */
  function applyImageLayout(option, media, isCheckbox = false) {
    // Skip if already restructured
    if (option.dataset.layoutApplied === 'true') return;

    try {
      const mainStack  = option.querySelector('.mw-stack--vertical');
      if (!mainStack) return;

      // Move media element before content stack (image left, content right)
      if (media && mainStack.contains(media)) {
        option.insertBefore(media, mainStack);
      }

      // Set flex order via data attribute for CSS to act on
      // (CSS handles order:1 for media, order:2 for stack already)
      if (media) media.dataset.seMedia = 'true';

      // Checkbox-specific: Apply inline styles to label and checkbox for proper alignment
      if (isCheckbox) {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const label = option.querySelector('label');
        
        // Apply label inline styles
        if (label) {
          Object.assign(label.style, {
            display: 'flex',
            alignItems: 'center',
            gap: '0px',
            margin: '0px',
            width: '100%',
            cursor: 'pointer',
            flexDirection: 'row',
            paddingRight: '40px'
          });
        }

        // Apply checkbox inline styles
        if (checkbox) {
          Object.assign(checkbox.style, {
            display: 'block',
            width: '18px',
            height: '18px',
            margin: '0px',
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            flexShrink: '0',
            accentColor: 'rgb(0, 0, 0)',
            cursor: 'pointer',
            zIndex: '10'
          });
        }
      }

      // For single-item options (no description): center-align content
      const stackItems  = mainStack.querySelectorAll('.mw-stack__item');
      const labelItem   = Array.from(stackItems).find(item => item.querySelector('label'));
      const descItem    = Array.from(stackItems).find(item =>
        !item.querySelector('label, .mw-option__media') && item.textContent.trim().length > 20
      );

      if (labelItem && !descItem) {
        Object.assign(labelItem.style, { width: '100%', display: 'flex', alignItems: 'center' });
      }

      // Mark as applied — continuous monitor checks this to avoid re-running
      option.dataset.layoutApplied = 'true';

    } catch (error) {
    }
  }

  /**
   * Apply image layout restructuring to all radio and checkbox cards
   * Replaces both applyRadioImageLayout and applyCheckboxImageLayout
   */
  function applyAllImageLayouts() {
    try {
      // Radio cards
      const allRadioContainers = document.querySelectorAll(
        '.mw-groups .mw-option--type-radio .mw-option__value'
      );
      allRadioContainers.forEach(option => {
        const media = option.querySelector('.mw-option__media, .option-media, .product-image, img');
        const hasRadio = option.querySelector('input[type="radio"]');
        if (media && hasRadio) applyImageLayout(option, media, false);
      });

      // Checkbox cards
      const allCheckboxContainers = document.querySelectorAll(
        '.mw-groups .mw-option--type-checkbox .mw-option__value'
      );
      allCheckboxContainers.forEach(option => {
        const media = option.querySelector('.mw-option__media, .option-media, .product-image, img');
        const hasCheckbox = option.querySelector('input[type="checkbox"]');
        if (media && hasCheckbox) applyImageLayout(option, media, true);
      });

    } catch (error) {
    }
  }

  function applyRadioImageLayout() {
    // OPTIMIZED: Direct call to universal function (no reason to have separate wrapper)
    applyAllImageLayouts();
  }

  function applyCheckboxImageLayout() {
    // OPTIMIZED: Direct call to universal function (no reason to have separate wrapper)
    // Consider removing these wrappers entirely and calling applyAllImageLayouts() directly
    applyAllImageLayouts();
  }

  function setLayoutPreset(preset) {
    const presets = {
      modern:   { imagePosition: 'left', imageSize: 'medium', layoutStyle: 'card',    spacing: 'normal' },
      compact:  { imagePosition: 'left', imageSize: 'small',  layoutStyle: 'minimal', spacing: 'tight'  },
      showcase: { imagePosition: 'left', imageSize: 'large',  layoutStyle: 'showcase',spacing: 'loose'  },
      grid:     { imagePosition: 'top',  imageSize: 'medium', layoutStyle: 'grid',    spacing: 'normal' }
    };
    if (presets[preset]) Object.assign(LAYOUT_OPTIONS, presets[preset]);
  }


  // ===== CONTINUOUS MONITOR =====
  /*
   * Polls every 3 seconds for up to 24 seconds after initialization.
   * Catches edge cases where APO re-renders partial content that
   * MutationObserver picks up but our debouncedRerun misses.
   *
   * Uses data-attribute checks (not inline style checks) to avoid
   * re-applying layout on every cycle.
   */
  function createContinuousMonitor() {
    if (continuousMonitorInterval) {
      clearInterval(continuousMonitorInterval);
      continuousMonitorInterval = null;
    }

    let checksCompleted = 0;
    // OPTIMIZATION: Track counts instead of re-querying entire DOM with complex selectors
    let lastRadioCount = 0, lastCheckboxCount = 0, lastRadioInputCount = 0, lastCheckboxInputCount = 0, lastFormStepCount = 0;

    function verifyAndFix() {
      try {
        checksCompleted++;
        let needsWork = false;

        // Query parent ONCE instead of 5 times with complex selectors
        const groups = document.querySelector('.mw-groups');
        if (!groups) return;

        // OPTIMIZATION: Delta-checking via counts (avoids :not() complex selectors)
        // Only re-apply when card count changes = new cards added

        // 1. Radio cards — count indicates if new radios added
        const radioCardCount = groups.querySelectorAll('.mw-option--type-radio .mw-option__value').length;
        if (radioCardCount !== lastRadioCount) {
          applyAllImageLayouts();
          lastRadioCount = radioCardCount;
          needsWork = true;
        }

        // 2. Checkbox cards
        const checkboxCardCount = groups.querySelectorAll('.mw-option--type-checkbox .mw-option__value').length;
        if (checkboxCardCount !== lastCheckboxCount) {
          applyAllImageLayouts();
          lastCheckboxCount = checkboxCardCount;
          needsWork = true;
        }

        // 3. Update visual selection states if detectedRadios is available
        if (prescriptionToggleState) {
          const checkedRadio = document.querySelector(
            `.mw-groups input[name="${prescriptionToggleState.groupName}"]:checked`
          );
          if (checkedRadio) updateRadioSelectionStates(checkedRadio);
        }

        // 4. Check radio/checkbox inputs — simpler count check (no :not() selector)
        const radioInputCount = groups.querySelectorAll('input[type="radio"]').length;
        const checkboxInputCount = groups.querySelectorAll('input[type="checkbox"]').length;

        if (radioInputCount !== lastRadioInputCount) {
          makeRadioCardsClickable();
          lastRadioInputCount = radioInputCount;
          needsWork = true;
        }

        if (checkboxInputCount !== lastCheckboxInputCount) {
          makeCheckboxCardsClickable();
          lastCheckboxInputCount = checkboxInputCount;
          needsWork = true;
        }

        // 5. Form steps — count-based check
        const multistepContainers = document.querySelectorAll('.prescription-multistep .mw-group__options').length;
        if (multistepContainers !== lastFormStepCount) {
          detectAndMarkFormSteps();
          lastFormStepCount = multistepContainers;
          needsWork = true;
        }

        // Stop early if stable for 3 consecutive checks
        if (!needsWork && checksCompleted >= 3) {
          clearInterval(continuousMonitorInterval);
          continuousMonitorInterval = null;
        }

      } catch (error) {
      }
    }

    // OPTIMIZATION: Schedule with requestIdleCallback for non-blocking execution
    const scheduleMonitor = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(verifyAndFix, { timeout: 3000 }); // Run during idle time, timeout after 3s
      } else {
        verifyAndFix(); // Fallback for older browsers
      }
    };

    scheduleMonitor();
    continuousMonitorInterval = setInterval(scheduleMonitor, 3000);

    // Hard stop after 24 seconds (8 checks max)
    setTimeout(() => {
      if (continuousMonitorInterval) {
        clearInterval(continuousMonitorInterval);
        continuousMonitorInterval = null;
      }
    }, 24000);
  }


  // ===== ORCHESTRATION =====
  async function runCombinedSystem() {
    try {
      // IMPROVED: Check if .mw-groups already exists before waiting
      let mwGroups = document.querySelector('.mw-groups');
      
      if (!mwGroups) {
        // Only wait for essential element (mw-groups), others follow naturally
        // Increased timeout to 30s to accommodate slow APO implementations
        mwGroups = await waitForElement('.mw-groups', 30000);
      }

      // Brief settle delay — APO may still be applying its own styles
      await settle(500);

      // 1. Classify prescription fields
      detectPrescriptionFields();

      // 2. Mark form steps (for auto-height)
      detectAndMarkFormSteps();

      // 3. Set layout preset (stores to LAYOUT_OPTIONS — used by future calls)
      setLayoutPreset('modern');

      // 4. Wait for radio inputs to exist (optional - some pages don't have radios)
      try {
        await waitForElements(
          ['.mw-groups input[type="radio"]'],
          5000
        );
      } catch (e) {
        // No radios found — that's okay, continue (some pages use only other option types)
        SporteyesLogger.log('No radio inputs found - continuing with other elements');
      }

      // 5. Apply image layout DOM restructuring (radio + checkbox)
      applyAllImageLayouts();

      // 6. Wait for prescription toggle infrastructure (optional)
      await settle(500);
      if (document.querySelector('.mw-group__progress')) {
        initPrescriptionToggle();
      }

      // 7. Wait and initialize selection states for all radio groups
      await settle(500);
      if (document.querySelectorAll('.mw-groups input[type="radio"]').length > 0) {
        initializeAllRadioStates();
      }

      // 8. Wait and enhance card clickability
      await settle(500);
      makeRadioCardsClickable();
      makeCheckboxCardsClickable();

      // 9. Start continuous monitor (catch any missed cards)
      createContinuousMonitor();

    } catch (error) {
      // Fallback: try again with longer delay (APO may have been very slow)
      // Log as WARNING (not ERROR) since fallback usually recovers
      SporteyesLogger.warn('runCombinedSystem timeout — attempting recovery', error);

      await settle(3000);
      try {
        detectPrescriptionFields();
        setLayoutPreset('modern');
        applyAllImageLayouts();
        if (document.querySelector('.mw-group__progress')) initPrescriptionToggle();
        await settle(500);
        initializeAllRadioStates();
        await settle(500);
        makeRadioCardsClickable();
        makeCheckboxCardsClickable();
        createContinuousMonitor();
        
        // Fallback succeeded - log success (dev mode only)
        SporteyesLogger.log('runCombinedSystem recovered successfully via fallback');
        
      } catch (e) {
        // Only log as CRITICAL error if fallback ALSO fails (actual failure, not just slow load)
        SporteyesLogger.critical('runCombinedSystem fallback also failed - manual intervention needed', e, {
          mwGroupsFound: document.querySelectorAll('.mw-groups').length,
          readyState: document.readyState,
          errorMessage: e?.message,
          timestamp: new Date().toISOString()
        });
      }
    }
  }


  // ===== ALL RADIO STATES INITIALIZATION =====
  function initializeAllRadioStates() {
    try {
      const allRadios = document.querySelectorAll('.mw-groups input[type="radio"]');

      allRadios.forEach(radio => {
        // Skip if listener already attached (prevents accumulation on APO re-render)
        if (radio.hasAttribute('data-listener-attached')) return;
        radio.setAttribute('data-listener-attached', 'true');

        const groupName = radio.name;
        // Skip the prescription group — universalToggle manages it
        if (prescriptionToggleState && groupName === prescriptionToggleState.groupName) return;

        radio.addEventListener('change', function () {
          setTimeout(() => updateRadioSelectionStates(this), 300);
        }, { passive: true });

        if (radio.checked) {
          setTimeout(() => updateRadioSelectionStates(radio), 500);
        }
      });
    } catch (error) {
    }
  }


  // ===== INTELLIGENT INITIALIZATION WITH RETRY =====
  let initAttempts = 0;
  const MAX_INIT_ATTEMPTS = 3;

  function intelligentInit() {
    initAttempts++;
    const hasRequired = document.querySelector('.mw-groups');

    if (hasRequired || initAttempts >= MAX_INIT_ATTEMPTS) {
      runCombinedSystem();
    } else {
      // Exponential backoff: 1s, 2s, 3s
      setTimeout(intelligentInit, 1000 * initAttempts);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(intelligentInit, 100));
  } else if (document.readyState === 'interactive') {
    setTimeout(intelligentInit, 100);
  } else {
    intelligentInit();
  }


  // ===== APO RE-RENDER HANDLER =====
  /*
   * Lightweight re-init on APO re-renders (color swatch switch, variant change).
   * Does NOT wait 15s for elements — assumes elements exist and quickly patches
   * any newly-rendered cards that lost their enhancements.
   */
  const debouncedRerun = debounce(() => {
    try {
      // Immediately restore clickability (most visible symptom of re-render)
      makeRadioCardsClickable();
      makeCheckboxCardsClickable();

      // Re-classify prescription fields and form steps on new DOM
      detectPrescriptionFields();
      detectAndMarkFormSteps();

      // Re-apply layout DOM restructuring to any newly-rendered cards
      applyRadioImageLayout();
      applyCheckboxImageLayout();

      // Check if prescription radio group still exists in DOM
      const groupStillExists = prescriptionToggleState &&
        document.querySelector(`.mw-groups input[name="${prescriptionToggleState.groupName}"]`);

      if (!groupStillExists) {
        // Radio group was fully replaced — need full re-detection
        prescriptionToggleState = null;
        runCombinedSystem();
      } else {
        // Group exists — just refresh visual states
        setTimeout(() => {
          initializeAllRadioStates();
          if (document.querySelector('.mw-group__progress')) initPrescriptionToggle();
        }, 200);
      }
    } catch (e) {
      prescriptionToggleState = null;
      runCombinedSystem();
    }
  }, 300);


  // ===== MAIN MUTATIONOBSERVER =====
  /*
   * Watches for APO re-renders. Fires on:
   *   Case 1: APO replaces children INSIDE .mw-groups/.mw-apo-container
   *           (most common — variant/swatch change)
   *   Case 2: A .mw-groups or .mw-apo-container element is freshly added
   *   Case 3: A container wrapping .mw-groups is injected (theme section)
   */
  let observerDebounceTimer = null;
  const mainObserver = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

      const target = mutation.target;

      // Case 1
      if (target.classList && (
        target.classList.contains('mw-groups') ||
        target.classList.contains('mw-apo-container') ||
        target.classList.contains('mw-group') ||
        target.classList.contains('mw-group__options')
      )) {
        clearTimeout(observerDebounceTimer);
        observerDebounceTimer = setTimeout(debouncedRerun, 100);
        return;
      }

      // Case 2 & 3
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.classList && (
          node.classList.contains('mw-groups') ||
          node.classList.contains('mw-apo-container')
        )) {
          clearTimeout(observerDebounceTimer);
          observerDebounceTimer = setTimeout(debouncedRerun, 100);
          return;
        }
        if (node.querySelector && node.querySelector('.mw-groups')) {
          clearTimeout(observerDebounceTimer);
          observerDebounceTimer = setTimeout(debouncedRerun, 100);
          return;
        }
      }
    }
  });

  // FIXED: Narrow from document.body to .mw-apo-container to reduce performance impact
  const mainObserverTarget = document.querySelector('.mw-apo-container') || document.body;
  mainObserver.observe(mainObserverTarget, { childList: true, subtree: true });


  // ===== PUBLIC API =====
  window.combinedSystem = {
    // Prescription toggle
    setupPrescription:   initPrescriptionToggle,
    detectRadios:        detectRadioStructure,
    detectFields:        detectPrescriptionFields,
    getCurrentStep:      () => currentStep,
    getDetectedRadios:   () => prescriptionToggleState,
    patterns:            FIELD_PATTERNS,

    // Image layout
    applyImageLayout:    applyRadioImageLayout,
    setPreset:           setLayoutPreset,
    setOptions:          (options) => Object.assign(LAYOUT_OPTIONS, options),

    // Checkbox layout
    applyCheckboxLayout: applyCheckboxImageLayout,

    // Card interaction
    makeCardsClickable:          makeRadioCardsClickable,
    makeCheckboxCardsClickable:  makeCheckboxCardsClickable,

    // Full system
    runAll: runCombinedSystem,

    version: '2.0.0'
  };

})();


/* =========================================================================
   COMPONENT: VARIANT GALLERY WEB COMPONENT
   Custom HTML element for interactive variant image slider
   <variant-gallery> with momentum-based drag, touch, and mouse support
   ========================================================================= */

if (!customElements.get('variant-gallery')) {
  customElements.define(
    'variant-gallery',
    class VariantGallery extends HTMLElement {
      constructor() {
        super();
        // ===== SLIDER CONFIGURATION =====
        this.CARD_WIDTH = 180;          // Individual card width in pixels
        this.GAP = 16;                  // Spacing between cards
        this.VISIBLE_CARDS = 3;         // Cards visible in viewport at once
        this.DRAG_THRESHOLD = 5;        // Minimum drag distance to activate slide
        this.currentSlide = 0;          // Current slide index
        this.isSliding = false;         // Animation in progress flag
        this.hasDragged = false;        // Drag gesture detected flag
        this.cachedElements = {};       // DOM element cache for performance
      }

      connectedCallback() {
        // Setup slider as soon as custom element is inserted into DOM
        // Use requestIdleCallback to avoid blocking initial render, but don't wait for 'load'
        if (document.readyState === 'loading') {
          // DOM still parsing — wait for DOMContentLoaded
          document.addEventListener('DOMContentLoaded', () => this.setupSlider(), { once: true });
        } else {
          // DOM already parsed — setup immediately (or defer to next microtask)
          requestAnimationFrame(() => this.setupSlider());
        }
      }

      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }

      // ===== SLIDER SETUP & INITIALIZATION =====
      // Initialize all slider DOM elements, dimensions, and event listeners
      setupSlider() {
        this.cachedElements = {
          container: this.querySelector('.variant-slider-container'),
          track: this.querySelector('.variant-slider-track'),
          viewport: this.querySelector('.variant-slider-viewport'),
          prevBtn: this.querySelector('.variant-slider-prev'),
          nextBtn: this.querySelector('.variant-slider-next'),
          cards: this.querySelectorAll('.product-variant-card')
        };
        
        const { container, track, viewport, prevBtn, nextBtn, cards } = this.cachedElements;
        if (!container || !track || !viewport || !prevBtn || !nextBtn || !cards.length) return;

        // Read actual card width from DOM layout (responsive) — fallback to 180px if not yet rendered
        const firstCard = cards[0];
        if (firstCard && firstCard.offsetWidth > 0) {
          this.CARD_WIDTH = firstCard.offsetWidth;
        } else {
          // Fallback to CSS-defined width as last resort
          this.CARD_WIDTH = 180;
        }

        // Calculate total widths for viewport and track
        const totalWidth = (this.CARD_WIDTH * this.VISIBLE_CARDS) + (this.GAP * (this.VISIBLE_CARDS - 1));
        const trackWidth = (this.CARD_WIDTH * cards.length) + (this.GAP * (cards.length - 1));
        
        viewport.style.width = `${totalWidth}px`;
        viewport.style.maxWidth = `${totalWidth}px`;
        track.style.width = `${trackWidth}px`;
        
        // Calculate maximum slide position
        this.maxSlides = Math.max(0, cards.length - this.VISIBLE_CARDS);
        this.currentSlide = 0;

        // Clone button nodes to remove old event listeners
        const newPrevBtn = prevBtn.cloneNode(true);
        const newNextBtn = nextBtn.cloneNode(true);
        prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
        nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
        
        this.cachedElements.prevBtn = newPrevBtn;
        this.cachedElements.nextBtn = newNextBtn;

        // Attach navigation button listeners
        newPrevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isSliding && this.currentSlide > 0) this.slidePrev();
        });

        newNextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isSliding && this.currentSlide < this.maxSlides) this.slideNext();
        });

        this.addTouchSupport(track);
        this.updateNavigationState();
      }

      // ===== NAVIGATION: PREVIOUS SLIDE =====
      // Move carousel one slide to the left
      slidePrev() {
        if (this.isSliding || this.currentSlide <= 0) return;
        this.currentSlide = Math.max(0, this.currentSlide - 1);
        this.updateSlidePosition();
        this.updateNavigationState();
      }

      // ===== NAVIGATION: NEXT SLIDE =====
      // Move carousel one slide to the right
      slideNext() {
        if (this.isSliding || this.currentSlide >= this.maxSlides) return;
        this.currentSlide = Math.min(this.maxSlides, this.currentSlide + 1);
        this.updateSlidePosition();
        this.updateNavigationState();
      }

      // ===== SLIDE POSITION UPDATE =====
      // Translate track to show current slide with optional smooth animation
      updateSlidePosition(smooth = true) {
        const track = this.cachedElements.track;
        if (!track) return;

        this.isSliding = smooth;
        const translateX = this.currentSlide * (this.CARD_WIDTH + this.GAP);
        
        track.style.transition = smooth ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
        track.style.transform = `translateX(-${translateX}px)`;
        
        // OPTIMIZATION: Use transitionend event instead of setTimeout
        // Waits for animation to complete naturally (GPU-accelerated, no timing mismatch)
        if (smooth) {
          const handleTransitionEnd = () => {
            this.isSliding = false;
            track.removeEventListener('transitionend', handleTransitionEnd);
          };
          track.addEventListener('transitionend', handleTransitionEnd, { once: true });
        }
      }

      setTrackPosition(translateX, smooth = false) {
        const track = this.cachedElements.track;
        if (!track) return;
        
        track.style.transition = smooth ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
        track.style.transform = `translateX(${translateX}px)`;
        
        // OPTIMIZATION: Use transitionend for smooth track positioning (GPU-accelerated)
        if (smooth) {
          const handleTransitionEnd = () => {
            track.removeEventListener('transitionend', handleTransitionEnd);
          };
          track.addEventListener('transitionend', handleTransitionEnd, { once: true });
        }
      }

      updateNavigationState() {
        const { prevBtn, nextBtn } = this.cachedElements;
        if (!prevBtn || !nextBtn) return;

        const atStart = this.currentSlide <= 0;
        const atEnd = this.currentSlide >= this.maxSlides;
        
        prevBtn.classList.toggle('disabled', atStart);
        prevBtn.disabled = atStart;
        nextBtn.classList.toggle('disabled', atEnd);
        nextBtn.disabled = atEnd;
      }

      // ===== TOUCH & DRAG SUPPORT (MOBILE & DESKTOP) =====
      // Implements drag gesture with momentum physics and elastic boundaries
      // Touch: mobile swipe, Mouse: click and drag
      // Drag threshold: 5px minimum to activate; velocity detection for momentum
      addTouchSupport(track) {
        let startX, currentX, isDragging, startTranslateX, currentTranslateX, velocityX, lastX, lastTime = 0;
        let cachedBounds = null;  // Cache bounds during drag to avoid repeated offsetWidth calls

        // Calculate boundaries for track movement (elastic constraints)
        const getTrackBounds = () => {
          const { viewport, cards } = this.cachedElements;
          const viewportWidth = viewport ? viewport.offsetWidth : this.CARD_WIDTH * 3 + this.GAP * 2;
          const totalTrackWidth = (this.CARD_WIDTH * cards.length) + (this.GAP * (cards.length - 1));
          const maxTranslateX = Math.max(0, totalTrackWidth - viewportWidth);
          return { min: -maxTranslateX, max: 0, trackWidth: totalTrackWidth, viewportWidth };
        };

        // Initialize drag gesture (mousedown or touchstart)
        const startDrag = (clientX) => {
          if (this.isSliding) return;
          startX = currentX = lastX = clientX;
          lastTime = Date.now();
          isDragging = true;
          this.hasDragged = false;
          velocityX = 0;
          const matrix = new DOMMatrix(window.getComputedStyle(track).transform);
          startTranslateX = currentTranslateX = matrix.m41;
          cachedBounds = getTrackBounds();  // Cache bounds at drag start
        };

        // Update drag position (mousemove or touchmove); apply elastic boundaries at edges
        const updateDrag = (clientX) => {
          if (!isDragging) return;
          currentX = clientX;
          const deltaX = currentX - startX;
          
          // Activate drag only after exceeding threshold (5px)
          if (!this.hasDragged && Math.abs(deltaX) > this.DRAG_THRESHOLD) {
            this.hasDragged = true;
            track.style.transition = 'none';
            track.style.cursor = 'grabbing';
            track.classList.add('is-dragging');
          }
          
          // Calculate new position with elastic boundary effect (30% resistance)
          if (this.hasDragged && cachedBounds) {
            const bounds = cachedBounds;  // Read from cache (no offsetWidth reflow)
            let newTranslateX = startTranslateX + deltaX;
            
            // Apply elastic effect at boundaries (30% friction)
            if (newTranslateX > bounds.max) {
              newTranslateX = bounds.max + ((newTranslateX - bounds.max) * 0.3);
            } else if (newTranslateX < bounds.min) {
              newTranslateX = bounds.min - ((bounds.min - newTranslateX) * 0.3);
            }
            
            currentTranslateX = newTranslateX;
            const currentTime = Date.now();
            const timeDelta = currentTime - lastTime;
            if (timeDelta > 0) {
              velocityX = (currentX - lastX) / timeDelta;
              lastX = currentX;
              lastTime = currentTime;
            }
            this.setTrackPosition(currentTranslateX, false);
          }
        };

        const endDrag = () => {
          if (!isDragging) return;
          isDragging = false;
          cachedBounds = null;  // Clear cache after drag ends
          track.style.cursor = 'grab';
          track.classList.remove('is-dragging');
          
          if (this.hasDragged) {
            const bounds = getTrackBounds();
            let finalTranslateX = currentTranslateX;
            
            if (Math.abs(velocityX) > 0.1) finalTranslateX += velocityX * 150;
            finalTranslateX = Math.max(bounds.min, Math.min(bounds.max, finalTranslateX));
            
            const cardStep = this.CARD_WIDTH + this.GAP;
            const targetSlide = Math.round(-finalTranslateX / cardStep);
            this.currentSlide = Math.max(0, Math.min(this.maxSlides, targetSlide));
            
            this.setTrackPosition(-targetSlide * cardStep, true);
            this.updateNavigationState();
          }
          setTimeout(() => this.hasDragged = false, 50);
        };

        track.addEventListener('touchstart', (e) => startDrag(e.touches[0].clientX), { passive: true });
        track.addEventListener('touchmove', (e) => { updateDrag(e.touches[0].clientX); if (this.hasDragged) e.preventDefault(); }, { passive: false });
        track.addEventListener('touchend', (e) => { endDrag(); if (this.hasDragged) e.preventDefault(); }, { passive: false });

        track.addEventListener('mousedown', (e) => {
          startDrag(e.clientX);
          const moveHandler = (e) => { updateDrag(e.clientX); if (this.hasDragged) e.preventDefault(); };
          const upHandler = (e) => {
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', upHandler);
            if (this.hasDragged) e.preventDefault();
            endDrag();
          };
          document.addEventListener('mousemove', moveHandler);
          document.addEventListener('mouseup', upHandler);
        });

        Object.assign(track.style, { userSelect: 'none', cursor: 'grab' });
        track.addEventListener('contextmenu', (e) => { if (isDragging) e.preventDefault(); });
        track.addEventListener('click', (e) => { if (this.hasDragged) { e.preventDefault(); e.stopPropagation(); return false; } }, true);

        this.addEventListener('keydown', (e) => {
          if (this.isSliding) return;
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.slidePrev(); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); this.slideNext(); }
        });
        this.setAttribute('tabindex', '0');
      }

      handleVariantChange(selectedCard) {
        this.cachedElements.cards.forEach(card => card.classList.remove('selected'));
        selectedCard.classList.add('selected');
        
        const cardIndex = Array.from(this.cachedElements.cards).indexOf(selectedCard);
        if (cardIndex >= 0) {
          const targetSlide = Math.max(0, Math.min(this.maxSlides, cardIndex - 1));
          if (targetSlide !== this.currentSlide) {
            this.currentSlide = targetSlide;
            this.updateSlidePosition();
            this.updateNavigationState();
          }
        }
      }
    }
  );
}

// VariantGallery auto-initializes via connectedCallback() — no manual init needed


/*
=============================================================================
SYSTEM: PD GUIDE LINK INJECTOR
Pattern-based detection of Pupil Distance (PD) fields and injection of 
measurement guide link. Creates modal system for displaying guides.
Links are automatically injected; modals created globally by this system.
=============================================================================
*/

(function() {
  'use strict';

  const CONFIG = {
    linkText: 'Guide to measure PD (Pupil Distance)',  // Text displayed as link
    linkClass: 'pd-guide-link',
    // Label text patterns for PD field detection (regex patterns - comprehensive coverage)
    labelPatterns: [
      /PD\s*\(/i,                     // Matches "PD (", "PD(", "pd (" - original pattern 1
      /PD\s+DISTANCE/i,               // Matches "PD Distance" - original pattern 5
      /PUPIL\s+DISTANCE/i             // Matches "Pupil Distance", "pupil distance" - covers patterns 2,3,4
    ]
  };

  /**
   * Find PD input field by searching labels for pattern matches
   * Uses multiple strategies: 'for' attribute, parent traversal, next sibling, data attributes
   * ENHANCED: More flexible matching, searches deeper, checks multiple container types
   */
  function findPDInputField() {
    // Strategy 1: Look for data-pd-field attribute (most reliable, if set)
    let input = document.querySelector('[data-pd-field="true"], input[data-field-type="pd"]');
    if (input) {
      return input;
    }

    // Strategy 2: Get all labels and search for PD patterns
    const labels = document.querySelectorAll('.mw-label, label, .mw-text--style-strong, .mw-option__label, [role="label"]');
    
    for (const label of labels) {
      const labelText = label.textContent.trim().toUpperCase();
      
      // Use CONFIG.labelPatterns (not PD_DETECTOR) - check patterns as 'some' match
      const isPDLabel = CONFIG.labelPatterns.some(pattern => pattern.test(labelText));
      
      if (isPDLabel) {
        // Method 1: Look for 'for' attribute (standard HTML)
        const forAttr = label.getAttribute('for');
        if (forAttr) {
          input = document.getElementById(forAttr);
          if (input) {
            return input;
          }
        }
        
        // Method 2: Check siblings and nearby inputs
        let searchElement = label;
        for (let i = 0; i < 5; i++) {
          searchElement = searchElement.nextElementSibling;
          if (!searchElement) break;
          
          input = searchElement.querySelector?.('input[type="text"], input[type="number"], input:not([type]), input[type="hidden"]');
          if (input) {
            return input;
          }
        }
        
        // Method 3: Search parent container
        const container = label.closest('.mw-option, .mw-stack, .form-field, .mw-option__container, .stack');
        if (container) {
          input = container.querySelector('input[type="text"], input[type="number"], input:not([type])');
          if (input && input.offsetHeight > 0) {
            return input;
          }
        }
        
        // Method 4: Search parent's parent
        if (label.parentElement?.parentElement) {
          input = label.parentElement.parentElement.querySelector('input[type="text"], input[type="number"], input:not([type])');
          if (input && input.offsetHeight > 0) {
            return input;
          }
        }
      }
    }
    
    // Strategy 3: Fallback - search all inputs with aria-label containing PD
    input = document.querySelector('input[aria-label*="PD"], input[aria-label*="Pupil"], input[placeholder*="PD"], input[placeholder*="Pupil"]');
    if (input) {
      return input;
    }
    
    return null;
  }

  /**
   * Create and inject the PD guide link after the PD input field
   */
  function injectPDGuideLink() {
    // Try to find the PD input field by label
    const pdInput = findPDInputField();
    
    if (!pdInput) {
      return false;
    }

    // Check if link already exists
    if (document.querySelector(`.${CONFIG.linkClass}`)) {
      return true;
    }

    // Find the container to insert the link
    let container = pdInput.closest('.mw-option__container');
    if (!container) {
      container = pdInput.closest('.mw-stack__item');
    }
    
    if (!container) {
      return false;
    }

    const linkWrapper = document.createElement('div');
    linkWrapper.className = 'mw-stack__item pd-guide-link-wrapper';
    linkWrapper.style.marginTop = '8px';

    const link = document.createElement('a');
    link.href = '#';
    link.className = CONFIG.linkClass;
    link.textContent = CONFIG.linkText;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof window.openPDGuideModal === 'function') window.openPDGuideModal();
    });

    linkWrapper.appendChild(link);

    // Insert the link after the container
    const parentStack = container.closest('.mw-stack');
    if (parentStack) {
      // Find the last stack item and insert after it
      const stackItems = parentStack.querySelectorAll('.mw-stack__item');
      const lastItem = stackItems[stackItems.length - 1];
      if (lastItem) {
        lastItem.parentNode.insertBefore(linkWrapper, lastItem.nextSibling);
      } else {
        parentStack.appendChild(linkWrapper);
      }
    } else {
      // Fallback: insert after the input's parent
      container.parentNode.insertBefore(linkWrapper, container.nextSibling);
    }

    return true;
  }

  // Clean retry pattern using MutationObserver for instant detection.
  // Instead of polling 3 times (500ms, 1500ms, 3000ms), watch for PD field presence.
  // Stops watching after 3.5s timeout to prevent memory leak.
  (function tryInject() {
    // First attempt
    if (injectPDGuideLink()) {
      return; // Success — stop here
    }

    // Create observer to watch for PD field appearing in DOM
    let pdObserverTimeout = null;
    const pdObserver = new MutationObserver(() => {
      // Try injection on every DOM change
      if (injectPDGuideLink()) {
        pdObserver.disconnect();
        if (pdObserverTimeout) clearTimeout(pdObserverTimeout);
      }
    });

    // FIXED: Narrow from document.body to .mw-apo-container to reduce observer overhead
    const pdObserverTarget = document.querySelector('.mw-apo-container') || document.body;
    pdObserver.observe(pdObserverTarget, { childList: true, subtree: true });

    // Safety timeout: stop watching after 3.5 seconds (prevents infinite observer)
    pdObserverTimeout = setTimeout(() => {
      pdObserver.disconnect();
    }, 3500);
  })();

})();


/**
 * =========================================================================
 * COMPONENT: PD GUIDE MODAL - Global Modal System
 * Creates and manages the modal popup (loaded globally on product pages)
 * Separate from the link injection to avoid modal being inside custom blocks
 * =========================================================================
 */

(function() {
  'use strict';

  const CONFIG = {
    modalId: 'pd-guide-modal',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0699/3539/9095/files/measuring_pd.webp' // Update with your actual image URL
  };

  /**
   * Create and inject the modal HTML at the body level (not inside any custom block)
   * CRITICAL: Uses setTimeout to ensure modal is added AFTER all page content loads
   */
  function createModal() {
    if (document.getElementById(CONFIG.modalId)) return;

    setTimeout(() => {
      if (document.getElementById(CONFIG.modalId)) return;

      const modal = document.createElement('div');
      Object.assign(modal, {id: CONFIG.modalId, className: 'pd-modal'});
      modal.style.display = 'none';

      const overlay = document.createElement('div');
      overlay.className = 'pd-modal-overlay';

      const content = document.createElement('div');
      content.className = 'pd-modal-content';

      const closeBtn = document.createElement('button');
      closeBtn.className = 'pd-modal-close';
      closeBtn.setAttribute('aria-label', 'Close modal');
      closeBtn.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'pd-modal-image-wrapper';

      const img = document.createElement('img');
      Object.assign(img, {src: CONFIG.imageUrl, alt: 'Guide to measure PD (Pupil Distance)', loading: 'lazy'});

      imageWrapper.appendChild(img);
      content.append(closeBtn, imageWrapper);
      modal.append(overlay, content);

      if (document.body) document.body.appendChild(modal);
      setupModalEventListeners();
    }, 0);
  }

  function setupModalEventListeners() {
    const modal = document.getElementById(CONFIG.modalId);
    if (!modal) return;

    const elements = {
      overlay: modal.querySelector('.pd-modal-overlay'),
      closeBtn: modal.querySelector('.pd-modal-close'),
      content: modal.querySelector('.pd-modal-content')
    };

    if (elements.closeBtn) {
      elements.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closePDGuideModal();
      });
    }

    if (elements.overlay) {
      elements.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        closePDGuideModal();
      });
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closePDGuideModal();
    });

    if (elements.content) {
      elements.content.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  window.openPDGuideModal = function() {
    const modal = document.getElementById(CONFIG.modalId);
    if (modal) {
      modal.style.display = 'flex';
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    } else {
      createModal();
      setTimeout(window.openPDGuideModal, 100);
    }
  };

  window.closePDGuideModal = function() {
    const modal = document.getElementById(CONFIG.modalId);
    if (modal) {
      modal.style.display = 'none';
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  };

  // Initialize modal on page load (creates it but doesn't show — shown only on demand)
  createModal();

})();

/* ======================================================================= */
/* END OF MERGED FILE */
/* Original files: sporteyes-custom.js + variant-gallery.js */
/* All functionality preserved. Zero modifications to logic. */
/* ======================================================================= */
